import axios from "axios";
import { API_URL } from "../constants";
import { iframeMessaging } from "@src/utils/iframeMessaging";
import { store } from "@src/redux/store";
import { sessionExpired } from "@src/redux/slice/authSlice";

const api = axios.create({
    baseURL: API_URL,
});

/* -----------------------------------------
   TOKEN HELPERS
----------------------------------------- */
const getAccessToken = () => localStorage.getItem("accessToken");
const getRefreshToken = () => localStorage.getItem("refreshToken");

const setAccessToken = (token: string) => {
    localStorage.setItem("accessToken", token);
};

const clearSession = () => {
    // localStorage.removeItem("accessToken");
    // localStorage.removeItem("refreshToken");
    // store.dispatch(clearMessages());
    store.dispatch(sessionExpired());
};

/* -----------------------------------------
   REFRESH TOKEN LOGIC
----------------------------------------- */
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

const refreshAccessToken = async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
        throw new Error("No refresh token");
    }

    const response = await axios.post(
        `${API_URL}/api/token/refresh/`,
        { refresh: refreshToken },
    );

    const newAccessToken = response.data?.access;
    setAccessToken(newAccessToken);
    return newAccessToken;
};

/* -----------------------------------------
   REQUEST INTERCEPTOR
----------------------------------------- */
api.interceptors.request.use(
    (config) => {
        const isAuthRoute = config.url?.includes("/api/token");

        if (!isAuthRoute) {
            const token = getAccessToken();
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        }
        return config;
    },
    (error) => Promise.reject(error),
);

/* -----------------------------------------
   RESPONSE INTERCEPTOR
----------------------------------------- */
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (
            error.response?.status === 401 &&
            !originalRequest._retry
        ) {
            originalRequest._retry = true;

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({
                        resolve: (token: string) => {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                            resolve(api(originalRequest));
                        },
                        reject,
                    });
                });
            }

            isRefreshing = true;

            try {
                const newToken = await refreshAccessToken();
                processQueue(null, newToken);

                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return api(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                clearSession();

                if (iframeMessaging.isInIframe()) {
                    window.parent.postMessage(
                        {
                            type: "doctusmind-token-expired",
                            timestamp: Date.now(),
                        },
                        "*",
                    );
                }

                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    },
);

export default api;
